import type { FetchKey } from '@app/src/api/fetchHooks'
import { useFetchFacets } from '@app/src/api/fetchHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import { useAssessmentTemplatesWithSections } from '@app/src/hooks/useAssessmentTemplatesWithSections'
import { useFetchAssessmentSettings } from '@app/src/pages/Assessments/useFetchAssessmentSettings'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import React from 'react'
import { useIntl } from 'react-intl'
import FilterSection from '../FilterSection'
import AssessmentSelectorFilter from './AssessmentSelectorFilter'

interface AssessmentTemplateSelectorWithLevelFilterProps {
  fetchKey: FetchKey
  facetEndpoint: string
  allowedFilters: string[]
  assessmentFilterNamePrefix?: string
  assessmentTemplates?: AssessmentTemplate[]
  hideTemplateNameFilter?: boolean
}

const AssessmentTemplateSelectorWithLevelFilter: React.FC<AssessmentTemplateSelectorWithLevelFilterProps> = ({
  fetchKey,
  facetEndpoint,
  allowedFilters,
  assessmentFilterNamePrefix,
  assessmentTemplates,
  hideTemplateNameFilter,
}) => {
  const { formatMessage } = useIntl()
  const { hasPermission } = usePermissions()
  const userFilters = useGetApiQueryFilters(allowedFilters)
  const { assessmentSettings } = useFetchAssessmentSettings()
  const canAccessAssessmentTemplate = hasPermission(Permissions.ASSESSMENT_TEMPLATE_ACCESS)
  const enabledAssessmentTemplates = assessmentSettings?.filter(setting => setting.isActivated) ?? []
  const hasAssessmentsEnabled = Boolean(enabledAssessmentTemplates.length)
  const { assessmentTemplates: fetchedTemplates } = useAssessmentTemplatesWithSections()

  const templates = assessmentTemplates ?? fetchedTemplates
  const templateFilterName = assessmentFilterNamePrefix
    ? `${assessmentFilterNamePrefix}.assessmentTemplate.name`
    : 'assessmentTemplate.name'

  const {
    facets: [templateNameFacet = []],
  } = useFetchFacets({
    key: [fetchKey, templateFilterName],
    endpoint: facetEndpoint,
    facetsParam: [{ name: templateFilterName }],
    filter: [...userFilters],
  })

  if (!canAccessAssessmentTemplate || !hasAssessmentsEnabled) return null

  return (
    <FilterSection label={formatMessage({ id: 'schemas.filter.assessments' })} defaultCollapsed>
      <AssessmentSelectorFilter
        templates={templates}
        facetOptions={templateNameFacet}
        formatMessage={formatMessage}
        prefix={assessmentFilterNamePrefix}
        fetchKey={fetchKey}
        facetEndpoint={facetEndpoint}
        hideTemplateNameFilter={hideTemplateNameFilter}
      />
    </FilterSection>
  )
}

export default AssessmentTemplateSelectorWithLevelFilter
